import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Controller, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import {
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  CircularProgress,
  FormControl,
  FormHelperText,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Divider,
  Alert,
  IconButton,
  InputAdornment,
  FormControlLabel,
  Switch,
} from '@mui/material';
import {
  Save as SaveIcon,
  ArrowBack as ArrowBackIcon,
  Delete as DeleteIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import { Item, ItemFormData } from '../types/item.types';
import { itemService } from '../services/item.service';

// Define form validation schema
type ItemFormValues = {
  itemCode: string;
  itemName: string;
  description: string; // Make required in form, but can be empty
  itemType: 'SERVICE' | 'INVENTORY';
  itemGroup: 'PHARMACY' | 'CONSUMABLE' | 'CONSULTATION' | 'THERAPIES' | 'INVESTIGATION' | 'STATIONERY' | 'OTHERS';
  itemSubGroup: 'LEHA_MAJOON' | 'SAFOOF' | 'TABLETS' | 'SHARBATH' | 'PRODUCT_NAME' | '';
  unitOfMeasure: string;
  costPrice: number | null;
  sellingPrice: number | null;
  reorderLevel: number | null;
  isActive: boolean;
};


const itemFormSchema = yup.object().shape({
  itemCode: yup.string().required('Item code is required'),
  itemName: yup.string().required('Item name is required'),
  description: yup.string().optional(),
  itemType: yup.string().oneOf(['SERVICE', 'INVENTORY']).required('Item type is required'),
  itemGroup: yup
    .string()
    .oneOf(['PHARMACY', 'CONSUMABLE', 'CONSULTATION', 'THERAPIES', 'INVESTIGATION', 'STATIONERY', 'OTHERS'])
    .required('Item group is required'),
  itemSubGroup: yup
    .string()
    .oneOf(['LEHA_MAJOON', 'SAFOOF', 'TABLETS', 'SHARBATH', 'PRODUCT_NAME', ''])
    .optional(),
  unitOfMeasure: yup.string().required('Unit of measure is required'),
  costPrice: yup.number().min(0, 'Cost price cannot be negative').nullable(),
  sellingPrice: yup
    .number()
    .min(0, 'Selling price cannot be negative')
    .test(
      'is-greater-than-cost',
      'Selling price must be greater than or equal to cost price',
      function (value) {
        if (!value || !this.parent.costPrice) return true;
        return value >= this.parent.costPrice;
      }
    )
    .nullable(),
  reorderLevel: yup.number().min(0, 'Reorder level cannot be negative').integer().nullable(),
  isActive: yup.boolean().default(true),
});

// Define default form values
const getDefaultValues = (initialItemName?: string): ItemFormValues => {
  const itemType = 'SERVICE'; // Default to SERVICE for order dialog usage
  const prefix = itemType === 'SERVICE' ? 'SVC' : 'ITM';
  const random = Math.floor(1000 + Math.random() * 9000);
  
  return {
    itemCode: `${prefix}-${random}`,
    itemName: initialItemName || '',
    description: '',
    itemType: itemType,
    itemGroup: 'PHARMACY',
    itemSubGroup: '',
    unitOfMeasure: 'PCS',
    costPrice: 0,
    sellingPrice: 100,
    reorderLevel: null,
    isActive: true,
  };
};

// Common units of measure
const unitsOfMeasure = [
  'PCS', 'BOX', 'BTL', 'TAB', 'CAP', 'TUBE', 'PKT', 'VIAL', 'AMP', 'ML', 'L', 'MG', 'G', 'KG'
];

interface ItemFormProps {
  // Dialog mode props
  isDialogMode?: boolean;
  onSaved?: (item: Item) => void;
  onClose?: () => void;
  // Existing props for edit mode
  itemId?: string;
  // Initial values for pre-filling form
  initialItemName?: string;
}

const ItemForm: React.FC<ItemFormProps> = ({
  isDialogMode = false,
  onSaved,
  onClose,
  itemId,
  initialItemName
}) => {
  const navigate = useNavigate();
  const { id: paramId } = useParams<{ id?: string }>();
  const { enqueueSnackbar } = useSnackbar();
  
  // Use itemId prop in dialog mode, otherwise use URL param
  const id = isDialogMode ? itemId : paramId;
  const [loading, setLoading] = useState(!!id);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [item, setItem] = useState<Item | null>(null);
  const [costPriceManuallyChanged, setCostPriceManuallyChanged] = useState(false);

  const isEditMode = !!id;

  const {
    control,
    handleSubmit,
    reset,
    setValue,
    watch,
    formState: { errors, isDirty, isValid },
  } = useForm<ItemFormValues>({
    resolver: yupResolver(itemFormSchema) as any, // Type assertion to handle yup and react-hook-form type mismatch
    defaultValues: getDefaultValues(initialItemName),
    mode: 'onChange',
    shouldUnregister: false,
  });

  // Watch values for conditional rendering/validation
  const itemType = watch('itemType');
  const costPrice = watch('costPrice');

  // Auto-calculate selling price with 40% markup when cost price changes
  useEffect(() => {
    if (costPrice && costPrice > 0) {
      // For new items, always calculate markup
      // For edit mode, only calculate if user manually changed cost price
      if (!isEditMode || costPriceManuallyChanged) {
        const sellingPrice = costPrice * 1.4; // 40% markup
        setValue('sellingPrice', Math.round(sellingPrice * 100) / 100, { shouldDirty: true }); // Round to 2 decimal places
      }
    }
  }, [costPrice, setValue, isEditMode, costPriceManuallyChanged]);

  // Fetch item data if in edit mode
  useEffect(() => {
    if (!id) return;

    const fetchItem = async () => {
      try {
        setLoading(true);
        const data = await itemService.getItemById(id);
        setItem(data);
        
        // Map API response to form values
        const formValues: ItemFormValues = {
          itemCode: data.itemCode,
          itemName: data.itemName,
          description: data.description ?? '',
          itemType: data.itemType,
          itemGroup: data.itemGroup,
          itemSubGroup: data.itemSubGroup ?? '',
          unitOfMeasure: data.unitOfMeasure,
          costPrice: data.costPrice ?? null,
          sellingPrice: data.sellingPrice ?? null,
          reorderLevel: data.reorderLevel ?? null,
          isActive: data.isActive,
        };
        
        reset(formValues);
      } catch (error: any) {
        console.error('Error fetching item:', error);
        enqueueSnackbar(error.message || 'Failed to load item', { variant: 'error' });
        if (!isDialogMode) {
          navigate('/settings/items');
        }
      } finally {
        setLoading(false);
      }
    };

    fetchItem();
  }, [id, reset, navigate, enqueueSnackbar]);

  // Handle form submission
  const onSubmit = async (formData: ItemFormValues) => {
    // Convert form data to API format
    const data: ItemFormData = {
      ...formData,
      description: formData.description || undefined, // Convert empty string to undefined for API
      itemSubGroup: formData.itemSubGroup === '' ? undefined : formData.itemSubGroup,
      costPrice: formData.costPrice ?? undefined,
      sellingPrice: formData.sellingPrice ?? undefined,
      reorderLevel: formData.reorderLevel ?? undefined,
    };
    try {
      setSaving(true);
      setError(null);

      let savedItem: Item;
      if (isEditMode && id) {
        savedItem = await itemService.updateItem(id, data);
        enqueueSnackbar('Item updated successfully', { variant: 'success' });
      } else {
        savedItem = await itemService.createItem(data);
        enqueueSnackbar('Item created successfully', { variant: 'success' });
      }

      // Handle dialog mode vs page mode
      if (isDialogMode) {
        onSaved?.(savedItem);
      } else {
        navigate('/settings/items');
      }
    } catch (error: any) {
      console.error('Error saving item:', error);
      setError(error.message || 'Failed to save item');
    } finally {
      setSaving(false);
    }
  };

  // Handle status toggle
  const handleToggleStatus = async () => {
    if (!id || !item) return;

    try {
      setSaving(true);
      const newStatus = !item.isActive;
      const updatedItem = await itemService.toggleItemStatus(id, newStatus);
      setItem(updatedItem);
      setValue('isActive', newStatus, { shouldDirty: true });
      enqueueSnackbar(`Item ${newStatus ? 'activated' : 'deactivated'} successfully`, {
        variant: 'success',
      });
    } catch (error: any) {
      console.error('Error toggling item status:', error);
      enqueueSnackbar(error.message || 'Failed to update status', { variant: 'error' });
    } finally {
      setSaving(false);
    }
  };

  // Handle delete
  const handleDelete = async () => {
    if (!id || !window.confirm('Are you sure you want to delete this item?')) return;

    try {
      setSaving(true);
      await itemService.deleteItem(id);
      enqueueSnackbar('Item deleted successfully', { variant: 'success' });
      if (!isDialogMode) {
        navigate('/settings/items');
      }
    } catch (error: any) {
      console.error('Error deleting item:', error);
      enqueueSnackbar(error.message || 'Failed to delete item', { variant: 'error' });
    } finally {
      setSaving(false);
    }
  };

  // Generate item code
  const generateItemCode = () => {
    const prefix = itemType === 'SERVICE' ? 'SVC' : 'ITM';
    const random = Math.floor(1000 + Math.random() * 9000);
    setValue('itemCode', `${prefix}-${random}`, { shouldValidate: true });
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="300px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      {!isDialogMode && (
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate('/settings/items')}
            color="inherit"
          >
            Back to Items
          </Button>

        {isEditMode && item && (
          <Box display="flex" gap={2}>
            <FormControlLabel
              control={
                <Switch
                  checked={item.isActive}
                  onChange={handleToggleStatus}
                  color="primary"
                  disabled={saving}
                />
              }
              label={item.isActive ? 'Active' : 'Inactive'}
              labelPlacement="start"
            />
            <Button
              variant="outlined"
              color="error"
              startIcon={<DeleteIcon />}
              onClick={handleDelete}
              disabled={saving}
            >
              Delete
            </Button>
          </Box>
        )}
        </Box>
      )}

      <Card>
        <CardHeader
          title={isEditMode ? 'Edit Item' : 'Create New Item'}
          titleTypographyProps={{ variant: 'h5' }}
        />
        <Divider />
        <CardContent>
          {error && (
            <Alert severity="error" sx={{ mb: 3 }}>
              {error}
            </Alert>
          )}

          <form onSubmit={handleSubmit(onSubmit)}>
            <Grid container spacing={3}>
              {/* Item Code */}
              <Grid item xs={12} md={6}>
                <Controller
                  name="itemCode"
                  control={control}
                  render={({ field }) => (
                    <FormControl fullWidth error={!!errors.itemCode}>
                      <TextField
                        {...field}
                        label="Item Code"
                        variant="outlined"
                        disabled={saving || isEditMode}
                        error={!!errors.itemCode}
                        helperText={errors.itemCode?.message}
                        InputProps={{
                          endAdornment: !isEditMode && (
                            <InputAdornment position="end">
                              <IconButton
                                onClick={generateItemCode}
                                edge="end"
                                disabled={saving}
                                title="Generate Code"
                              >
                                <RefreshIcon />
                              </IconButton>
                            </InputAdornment>
                          ),
                        }}
                      />
                    </FormControl>
                  )}
                />
              </Grid>

              {/* Item Name */}
              <Grid item xs={12} md={6}>
                <Controller
                  name="itemName"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="Item Name"
                      variant="outlined"
                      fullWidth
                      disabled={saving}
                      error={!!errors.itemName}
                      helperText={errors.itemName?.message}
                    />
                  )}
                />
              </Grid>

              {/* Item Type */}
              <Grid item xs={12} md={6}>
                <Controller
                  name="itemType"
                  control={control}
                  render={({ field }) => (
                    <FormControl fullWidth error={!!errors.itemType}>
                      <InputLabel>Item Type</InputLabel>
                      <Select
                        {...field}
                        label="Item Type"
                        disabled={saving}
                      >
                        <MenuItem value="INVENTORY">Inventory</MenuItem>
                        <MenuItem value="SERVICE">Service</MenuItem>
                      </Select>
                      {errors.itemType && (
                        <FormHelperText>{errors.itemType.message}</FormHelperText>
                      )}
                    </FormControl>
                  )}
                />
              </Grid>

              {/* Item Group */}
              <Grid item xs={12} md={6}>
                <Controller
                  name="itemGroup"
                  control={control}
                  render={({ field }) => (
                    <FormControl fullWidth error={!!errors.itemGroup}>
                      <InputLabel>Item Group</InputLabel>
                      <Select
                        {...field}
                        label="Item Group"
                        disabled={saving}
                      >
                        <MenuItem value="PHARMACY">Pharmacy</MenuItem>
                        <MenuItem value="CONSUMABLE">Consumable</MenuItem>
                        <MenuItem value="CONSULTATION">Consultation</MenuItem>
                        <MenuItem value="THERAPIES">Therapies</MenuItem>
                        <MenuItem value="INVESTIGATION">Investigation</MenuItem>
                        <MenuItem value="STATIONERY">Stationery</MenuItem>
                        <MenuItem value="OTHERS">Others</MenuItem>
                      </Select>
                      {errors.itemGroup && (
                        <FormHelperText>{errors.itemGroup.message}</FormHelperText>
                      )}
                    </FormControl>
                  )}
                />
              </Grid>

              {/* Item Sub Group */}
              <Grid item xs={12} md={6}>
                <Controller
                  name="itemSubGroup"
                  control={control}
                  render={({ field }) => (
                    <FormControl fullWidth error={!!errors.itemSubGroup}>
                      <InputLabel>Item Sub Group</InputLabel>
                      <Select
                        {...field}
                        label="Item Sub Group"
                        disabled={saving}
                      >
                        <MenuItem value="">None</MenuItem>
                        <MenuItem value="LEHA_MAJOON">Leha/Majoon</MenuItem>
                        <MenuItem value="SAFOOF">Safoof</MenuItem>
                        <MenuItem value="TABLETS">Tablets</MenuItem>
                        <MenuItem value="SHARBATH">Sharbath</MenuItem>
                        <MenuItem value="PRODUCT_NAME">Product Name</MenuItem>
                      </Select>
                      {errors.itemSubGroup && (
                        <FormHelperText>{errors.itemSubGroup.message}</FormHelperText>
                      )}
                    </FormControl>
                  )}
                />
              </Grid>

              {/* Unit of Measure */}
              <Grid item xs={12} md={6}>
                <Controller
                  name="unitOfMeasure"
                  control={control}
                  render={({ field }) => (
                    <FormControl fullWidth error={!!errors.unitOfMeasure}>
                      <InputLabel>Unit of Measure</InputLabel>
                      <Select
                        {...field}
                        label="Unit of Measure"
                        disabled={saving}
                      >
                        {unitsOfMeasure.map((unit) => (
                          <MenuItem key={unit} value={unit}>
                            {unit}
                          </MenuItem>
                        ))}
                      </Select>
                      {errors.unitOfMeasure && (
                        <FormHelperText>{errors.unitOfMeasure.message}</FormHelperText>
                      )}
                    </FormControl>
                  )}
                />
              </Grid>

              {/* Cost Price */}
              <Grid item xs={12} md={6}>
                <Controller
                  name="costPrice"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="Cost Price"
                      type="number"
                      variant="outlined"
                      fullWidth
                      disabled={saving}
                      error={!!errors.costPrice}
                      helperText={errors.costPrice?.message}
                      value={field.value ?? ''}
                      onChange={(e) => {
                        const value = e.target.value === '' ? null : Number(e.target.value);
                        field.onChange(value);
                        // Mark that cost price was manually changed in edit mode
                        if (isEditMode) {
                          setCostPriceManuallyChanged(true);
                        }
                      }}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">₹</InputAdornment>
                        ),
                      }}
                    />
                  )}
                />
              </Grid>

              {/* Selling Price */}
              <Grid item xs={12} md={6}>
                <Controller
                  name="sellingPrice"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="Selling Price"
                      type="number"
                      variant="outlined"
                      fullWidth
                      disabled={saving}
                      error={!!errors.sellingPrice}
                      helperText={
                        errors.sellingPrice?.message ||
                        (costPrice && field.value && field.value < costPrice
                          ? 'Selling price should be greater than or equal to cost price'
                          : '')
                      }
                      value={field.value ?? ''}
                      onChange={(e) => {
                        const value = e.target.value === '' ? null : Number(e.target.value);
                        field.onChange(value);
                      }}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">₹</InputAdornment>
                        ),
                      }}
                    />
                  )}
                />
              </Grid>

              {/* Reorder Level */}
              <Grid item xs={12} md={6}>
                <Controller
                  name="reorderLevel"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="Reorder Level"
                      type="number"
                      variant="outlined"
                      fullWidth
                      disabled={saving}
                      error={!!errors.reorderLevel}
                      helperText={errors.reorderLevel?.message}
                      value={field.value ?? ''}
                      onChange={(e) => {
                        const value = e.target.value === '' ? null : Number(e.target.value);
                        field.onChange(value);
                      }}
                    />
                  )}
                />
              </Grid>

              {/* Description */}
              <Grid item xs={12}>
                <Controller
                  name="description"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="Description"
                      variant="outlined"
                      fullWidth
                      multiline
                      rows={3}
                      disabled={saving}
                      error={!!errors.description}
                      helperText={errors.description?.message}
                    />
                  )}
                />
              </Grid>

              {/* Submit Button */}
              <Grid item xs={12}>
                <Box display="flex" justifyContent="flex-end" gap={2} mt={2}>
                  <Button
                    variant="outlined"
                    color="primary"
                    onClick={() => isDialogMode ? onClose?.() : navigate('/settings/items')}
                    disabled={saving}
                  >
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    variant="contained"
                    color="primary"
                    startIcon={saving ? <CircularProgress size={20} /> : <SaveIcon />}
                    disabled={saving || !isValid || (!isDialogMode && !isDirty)}
                  >
                    {saving ? 'Saving...' : 'Save'}
                  </Button>
                </Box>
              </Grid>
            </Grid>
          </form>
        </CardContent>
      </Card>
    </Box>
  );
};

export default ItemForm;
